import type { TaskOutcomeContract } from '@erp/shared-types';
import {
  buildOutcomeCloseInput,
  outcomeCloseIsBlocked,
  outcomeReasonRequired,
  isReasonOnlyCancellation,
} from './taskOutcomeClose';
import { useTaskOutcomeContract } from 'src/api/rpc/hooks/tasks/useTaskDetail';
import { TaskOutcomeFields } from './TaskOutcomeFields';
import { useContext, useState } from 'react';
import { T, useGT, Var } from 'gt-react';
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import { isTerminalTaskStatus } from '@erp/shared-types';
import type { TaskResponse } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import {
  SelectDropdown,
  type SelectDropdownOption,
} from 'src/components/common/SelectDropdown';
import { toast } from 'src/components/ui/toast';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import {
  playbookGateBlockFrom,
  type PlaybookGateBlock,
} from './taskPlaybookGate';
import { AuthContext } from 'src/context/auth.context';
import {
  useAssignTask,
  useCancelTask,
  useCompleteTask,
  useFailTask,
  useUnassignTask,
} from 'src/api/rpc/hooks/tasks/useTaskDetail';

const UNASSIGNED_VALUE = '__unassigned__';
const ASSIGN_ME_VALUE = '__me__';
const OUTCOME_NONE_VALUE = '__none__';

type TerminalKind = 'complete' | 'fail' | 'cancel';

/**
 * Compact assignee control for the task review header toolbar.
 */
export function TaskAssigneeControl({
  task,
  users,
  agents,
}: {
  task: TaskResponse;
  users: Array<{ id: string; name: string }>;
  agents: Array<{ id: string; name: string }>;
}) {
  const gt = useGT();
  const { currentUser } = useContext(AuthContext);
  const assignTask = useAssignTask(task.id);
  const unassignTask = useUnassignTask(task.id);
  const terminal = isTerminalTaskStatus(task.status);
  const busy = assignTask.isPending || unassignTask.isPending;
  const isAssignedToMe = Boolean(
    currentUser && task.assigneeUserId === currentUser.id,
  );
  const meDisplay = gt('Me', {
    context: 'short display for current-user assignee on task review',
  });

  const assigneeOptions: SelectDropdownOption[] = [
    {
      id: UNASSIGNED_VALUE,
      label: gt('Unassigned', {
        context: 'task review assignee picker, clear assignee',
      }),
      display: gt('Unassigned', {
        context: 'task review assignee picker, clear assignee',
      }),
    },
    ...(currentUser
      ? [
          {
            id: ASSIGN_ME_VALUE,
            // "Assign to me" only makes sense when you are not already assignee.
            label: isAssignedToMe
              ? meDisplay
              : gt('Assign to me', {
                  context: 'task review assignee picker, assign current user',
                }),
            display: meDisplay,
          },
        ]
      : []),
    ...users
      .filter((user) => user.id !== currentUser?.id)
      .map((user) => ({
        id: `user:${user.id}`,
        label: user.name,
        display: user.name,
      })),
    ...agents.map((agent) => ({
      id: `agent:${agent.id}`,
      label: agent.name,
      display: agent.name,
    })),
  ];

  const selectedAssigneeId = task.assigneeAgentDefinitionId
    ? `agent:${task.assigneeAgentDefinitionId}`
    : task.assigneeUserId
      ? task.assigneeUserId === currentUser?.id
        ? ASSIGN_ME_VALUE
        : `user:${task.assigneeUserId}`
      : UNASSIGNED_VALUE;

  async function handleAssigneeChange(option: SelectDropdownOption | null) {
    if (!option || terminal) return;
    try {
      if (option.id === UNASSIGNED_VALUE) {
        await unassignTask.mutateAsync({ id: task.id });
      } else if (option.id === ASSIGN_ME_VALUE) {
        if (!currentUser) return;
        await assignTask.mutateAsync({
          id: task.id,
          assigneeType: 'user',
          assigneeUserId: currentUser.id,
        });
      } else if (option.id.startsWith('user:')) {
        await assignTask.mutateAsync({
          id: task.id,
          assigneeType: 'user',
          assigneeUserId: option.id.slice('user:'.length),
        });
      } else if (option.id.startsWith('agent:')) {
        await assignTask.mutateAsync({
          id: task.id,
          assigneeType: 'agent',
          assigneeAgentDefinitionId: option.id.slice('agent:'.length),
        });
      }
      toast.success(
        gt('Assignee updated', {
          context: 'toast after changing task assignee on review page',
        }),
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Failed to update assignee', {
              context: 'toast after task assignee update failed',
            }),
      );
    }
  }

  if (terminal) return null;

  return (
    <div className="flex w-full items-center gap-1.5">
      <span className="shrink-0 text-[11px] text-muted-foreground">
        <T context="label beside task review assignee picker">Assignee</T>
      </span>
      <SelectDropdown
        options={assigneeOptions}
        value={
          assigneeOptions.find((option) => option.id === selectedAssigneeId) ??
          null
        }
        onChange={(option) => {
          void handleAssigneeChange(option);
        }}
        placeholder={gt('Unassigned', {
          context: 'placeholder when task has no assignee on review page',
        })}
        searchPlaceholder={gt('Search…', {
          context: 'search box in task review assignee picker',
        })}
        size="sm"
        fullWidth={false}
        disabled={busy}
        className="min-w-36 w-full [&_button]:w-full [&_button]:min-w-0"
      />
    </div>
  );
}

/**
 * Close the task via Complete (primary) or Fail / Cancel (menu). Outcome and
 * reason are collected in a dialog only when closing — not as a permanent form.
 */
export function TaskCloseControls({ task }: { task: TaskResponse }) {
  const gt = useGT();
  const completeTask = useCompleteTask(task.id);
  const failTask = useFailTask(task.id);
  const cancelTask = useCancelTask(task.id);

  const [dialogKind, setDialogKind] = useState<TerminalKind | null>(null);
  const [outcome, setOutcome] = useState('');
  const [outcomeReason, setOutcomeReason] = useState('');
  const [outcomeValues, setOutcomeValues] = useState<Record<string, unknown>>(
    {},
  );
  const contractQuery = useTaskOutcomeContract(task.id, dialogKind !== null);
  const contract = contractQuery.data?.contract;
  const cancelWithReason = isReasonOnlyCancellation(contract, dialogKind);
  // Set when the server refused completion because a required playbook
  // procedure has no engagement evidence; the dialog then offers a waiver.
  const [playbookBlock, setPlaybookBlock] = useState<PlaybookGateBlock | null>(
    null,
  );
  const [waiveReason, setWaiveReason] = useState('');

  const busy = [completeTask, failTask, cancelTask].some(
    (mutation) => mutation.isPending,
  );

  if (isTerminalTaskStatus(task.status)) {
    return null;
  }

  function openDialog(kind: TerminalKind) {
    setOutcome('');
    setOutcomeReason('');
    setOutcomeValues({});
    setPlaybookBlock(null);
    setWaiveReason('');
    setDialogKind(kind);
  }

  async function confirmClose() {
    if (!dialogKind) return;
    if (!contract) return;
    try {
      const input = buildOutcomeCloseInput(
        task.id,
        contract,
        cancelWithReason,
        outcome,
        outcomeReason,
        outcomeValues,
      );
      const mutation = {
        complete: completeTask,
        fail: failTask,
        cancel: cancelTask,
      }[dialogKind];
      const request = playbookBlock
        ? { ...input, waivePlaybook: { reason: waiveReason.trim() } }
        : input;
      await mutation.mutateAsync(request);
      toast.success(
        {
          complete: gt('Task completed', {
            context: 'toast after completing a task on review page',
          }),
          fail: gt('Task failed', {
            context: 'toast after failing a task on review page',
          }),
          cancel: gt('Task cancelled', {
            context: 'toast after cancelling a task on review page',
          }),
        }[dialogKind],
      );
      setDialogKind(null);
    } catch (error) {
      void contractQuery.refetch();
      // The playbook gate: keep the dialog open and ask for a waiver reason
      // instead of surfacing the refusal as a dead-end error.
      const block =
        dialogKind === 'complete' ? playbookGateBlockFrom(error) : null;
      if (block) {
        setPlaybookBlock(block);
        return;
      }
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Failed to update task status', {
              context: 'toast after terminal task transition failed',
            }),
      );
    }
  }

  const waiveIncomplete = playbookBlock !== null && !waiveReason.trim();

  const dialogTitle = {
    complete: gt('Complete task', {
      context: 'dialog title when completing a task',
    }),
    fail: gt('Mark task failed', {
      context: 'dialog title when failing a task',
    }),
    cancel: gt('Cancel task', {
      context: 'dialog title when cancelling a task',
    }),
  }[dialogKind ?? 'cancel'];

  return (
    <>
      <div className="flex items-center" data-testid="task-close-controls">
        <Button
          size="sm"
          className="rounded-r-none"
          disabled={busy}
          onClick={() => openDialog('complete')}
        >
          <CheckIcon className="size-3.5" />
          <T context="button, complete the task">Complete</T>
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              className="rounded-l-none border-l border-l-primary-foreground/20 px-2"
              disabled={busy}
              aria-label={gt('More close options', {
                context: 'accessible label for fail/cancel menu on task review',
              })}
            >
              <CaretDownIcon className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => openDialog('fail')}>
              <T context="menu item, mark the task failed">Fail</T>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openDialog('cancel')}>
              <T context="menu item, cancel the task">Cancel</T>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog
        open={dialogKind !== null}
        onOpenChange={(open) => {
          if (!open) setDialogKind(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <TaskContractLoadState query={contractQuery} />
            <TaskOutcomeSection
              taskId={task.id}
              contract={contract}
              cancelling={cancelWithReason}
              outcome={outcome}
              setOutcome={setOutcome}
              outcomeValues={outcomeValues}
              setOutcomeValues={setOutcomeValues}
              busy={busy}
            />
            <TaskReasonInput
              taskId={task.id}
              required={outcomeReasonRequired(contract, task.status)}
              outcomeReason={outcomeReason}
              setOutcomeReason={setOutcomeReason}
              busy={busy}
            />
            <PlaybookWaiverFields
              taskId={task.id}
              block={playbookBlock}
              reason={waiveReason}
              busy={busy}
              onChange={setWaiveReason}
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => setDialogKind(null)}
            >
              <T context="button, dismiss task close dialog">Back</T>
            </Button>
            <Button
              variant={closeButtonVariant(dialogKind)}
              disabled={
                busy ||
                waiveIncomplete ||
                outcomeCloseIsBlocked(
                  contract,
                  cancelWithReason,
                  outcome,
                  outcomeReason,
                )
              }
              onClick={() => void confirmClose()}
            >
              <TaskCloseLabel
                busy={busy}
                waiving={playbookBlock !== null}
                title={dialogTitle}
              />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function closeButtonVariant(
  kind: TerminalKind | null,
): 'destructive' | 'default' {
  return kind === 'fail' ? 'destructive' : 'default';
}

function TaskReasonInput({
  taskId,
  required,
  outcomeReason,
  setOutcomeReason,
  busy,
}: {
  taskId: string;
  required: boolean;
  outcomeReason: string;
  setOutcomeReason: (value: string) => void;
  busy: boolean;
}) {
  const gt = useGT();
  return (
    <div className="space-y-1">
      <Label htmlFor={`task-${taskId}-reason`} className="text-[11px]">
        <T context="label for free-text reason when closing a task">Reason</T>
      </Label>
      <Textarea
        id={`task-${taskId}-reason`}
        className="min-h-20 text-xs"
        value={outcomeReason}
        aria-required={required}
        disabled={busy}
        onChange={(e) => setOutcomeReason(e.target.value)}
        placeholder={
          required
            ? gt('Explain the result and its evidence', {
                context: 'required task outcome reason',
              })
            : gt('Optional notes for the close', {
                context: 'placeholder for outcome reason in task close dialog',
              })
        }
      />
      {/* Hook point: replace/augment with Kyle's retroactive feedback store when ready. */}
    </div>
  );
}

function TaskContractLoadState({
  query,
}: {
  query: ReturnType<typeof useTaskOutcomeContract>;
}) {
  return (
    <>
      {query.isPending && (
        <p>
          <T context="loading task outcome contract">
            Loading outcome requirements…
          </T>
        </p>
      )}
      {(query.error || query.data?.error) && (
        <p role="alert">{query.data?.error ?? query.error?.message}</p>
      )}
    </>
  );
}

function TaskOutcomeSection({
  taskId,
  contract,
  cancelling,
  outcome,
  setOutcome,
  outcomeValues,
  setOutcomeValues,
  busy,
}: {
  taskId: string;
  contract: TaskOutcomeContract | null | undefined;
  cancelling: boolean;
  outcome: string;
  setOutcome: (outcome: string) => void;
  outcomeValues: Record<string, unknown>;
  setOutcomeValues: (values: Record<string, unknown>) => void;
  busy: boolean;
}) {
  const gt = useGT();
  if (cancelling || !contract) return null;
  return (
    <>
      <div className="space-y-1">
        <Label htmlFor={`task-${taskId}-outcome`} className="text-[11px]">
          <T context="label for optional outcome when closing a task">
            Outcome
          </T>
        </Label>
        <Select
          value={outcome || undefined}
          onValueChange={(value) =>
            setOutcome(value === OUTCOME_NONE_VALUE ? '' : value)
          }
          disabled={busy}
        >
          <SelectTrigger id={`task-${taskId}-outcome`} className="h-8">
            <SelectValue
              placeholder={
                contract.outcomeRequired
                  ? gt('Select an outcome', {
                      context: 'required task outcome selection',
                    })
                  : gt('Optional', {
                      context: 'placeholder when no task outcome selected',
                    })
              }
            />
          </SelectTrigger>
          <SelectContent>
            {!contract.outcomeRequired && (
              <SelectItem value={OUTCOME_NONE_VALUE}>
                {gt('None', {
                  context: 'option that clears the optional task outcome',
                })}
              </SelectItem>
            )}
            {contract.outcomes.map((value) => (
              <SelectItem key={value} value={value}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <TaskOutcomeFields
        fields={contract.fields}
        outcome={outcome}
        values={outcomeValues}
        disabled={busy}
        onChange={setOutcomeValues}
      />
    </>
  );
}

function TaskCloseLabel({
  busy,
  waiving,
  title,
}: {
  busy: boolean;
  waiving: boolean;
  title: string;
}) {
  if (busy) return <T context="button label while closing a task">Saving…</T>;
  if (waiving)
    return (
      <T context="button, complete a task waiving its unmet required procedures">
        Complete Anyway
      </T>
    );
  return title;
}

function PlaybookWaiverFields({
  taskId,
  block: playbookBlock,
  reason: waiveReason,
  busy,
  onChange: setWaiveReason,
}: {
  taskId: string;
  block: PlaybookGateBlock | null;
  reason: string;
  busy: boolean;
  onChange: (value: string) => void;
}) {
  const gt = useGT();
  return (
    <>
      {' '}
      {playbookBlock ? (
        <div className="space-y-2">
          <AlertContainer>
            <AlertDescription>
              <T context="task close dialog, playbook gate explanation">
                Required procedure(s) not engaged for this task:{' '}
                <Var>{playbookBlock.unmet.map((u) => u.title).join(', ')}</Var>.
                Give a reason to complete anyway; the waiver is recorded on the
                task.
              </T>
            </AlertDescription>
          </AlertContainer>
          <div className="space-y-1">
            <Label
              htmlFor={`task-${taskId}-waive-reason`}
              className="text-[11px]"
            >
              <T context="label for waiver reason when completing past the playbook gate">
                Why completion can proceed without this procedure
              </T>
            </Label>
            <Textarea
              id={`task-${taskId}-waive-reason`}
              className="min-h-16 text-xs"
              value={waiveReason}
              disabled={busy}
              onChange={(e) => setWaiveReason(e.target.value)}
              placeholder={gt('e.g. Posted by hand while the SOP was down', {
                context: 'placeholder for playbook waiver reason',
              })}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}
